import type {
  ReportSankeyLink,
  ReportSankeyNode,
} from '@/modules/analysis/stores/types'

// 'received' and 'spent' draw the All view. The other views fold an account
// into one 'account' node, or into 'from' and 'to' for transfers.
export type FlowRole =
  | 'payer'
  | 'received'
  | 'spent'
  | 'account'
  | 'from'
  | 'to'
  | 'payee'

export type FlowKind = 'in' | 'kept' | 'between' | 'out'

export interface FlowOfFunds {
  nodes: ReportSankeyNode[]
  links: ReportSankeyLink[]
}

export interface FlowColumn {
  role: FlowRole
  heading: string
}

export const FLOW_COLUMNS: FlowColumn[] = [
  { role: 'payer', heading: 'Payers' },
  { role: 'received', heading: 'Came in to' },
  { role: 'spent', heading: 'Paid out from' },
  { role: 'account', heading: 'Account' },
  { role: 'from', heading: 'From account' },
  { role: 'to', heading: 'To account' },
  { role: 'payee', heading: 'Payees' },
]

export const FLOW_KINDS: FlowKind[] = ['in', 'kept', 'between', 'out']

const FLOW_KIND_LABELS: Record<FlowKind, string> = {
  in: 'Money in',
  kept: 'Spent from the same account',
  between: 'Between own accounts',
  out: 'Money out',
}

const ACCOUNT_ROLE_SUFFIX = / \((received|spent|account|from|to)\)( \d+)?$/
const OTHER_BUCKET_NAMES = new Set(['Other payers', 'Other payees'])

export function flowKindLabel(kind: FlowKind): string {
  return FLOW_KIND_LABELS[kind]
}

function isFlowRole(role: unknown): role is FlowRole {
  return FLOW_COLUMNS.some((column) => column.role === role)
}

export function hasFlowOfFundsRoles(nodes: { role?: string }[]): boolean {
  return nodes.length > 0 && nodes.every((node) => isFlowRole(node.role))
}

// Only call once hasFlowOfFundsRoles has vouched for every node.
export function roleOf(node: ReportSankeyNode): FlowRole {
  return node.role as FlowRole
}

export function displayLabel(node: ReportSankeyNode): string {
  return node.label ?? node.name
}

export function isOtherBucket(node: ReportSankeyNode): boolean {
  return OTHER_BUCKET_NAMES.has(node.name)
}

// One own account shows up as "X (received)" and "X (spent)", or as
// "X (from)" and "X (to)" in the transfers view.
export function accountKey(node: ReportSankeyNode): string {
  return node.name.replace(ACCOUNT_ROLE_SUFFIX, '')
}

export function nodesByName(flow: FlowOfFunds): Map<string, ReportSankeyNode> {
  return new Map(flow.nodes.map((node) => [node.name, node]))
}

export function flowKindOf(
  link: ReportSankeyLink,
  byName: Map<string, ReportSankeyNode>,
): FlowKind {
  const source = byName.get(link.source)
  const target = byName.get(link.target)
  if (!source || !target) return 'between'
  if (roleOf(source) === 'payer') return 'in'
  if (roleOf(target) === 'payee') return 'out'
  if (roleOf(source) === 'from') return 'between'
  return accountKey(source) === accountKey(target) ? 'kept' : 'between'
}

export function presentColumns(flow: FlowOfFunds): FlowColumn[] {
  return FLOW_COLUMNS.filter((column) =>
    flow.nodes.some((node) => roleOf(node) === column.role),
  )
}
